import type { AuthError, Session } from '@supabase/supabase-js'
import type React from 'react'
import { createContext, useContext, useEffect, useRef, useState } from 'react'
import { type AuthUser, supabase } from '../lib/supabase'
import { identifyUser, resetUser, trackEvent } from '../utils/mixpanel'

interface AuthContextType {
  user: AuthUser | null
  session: Session | null
  loading: boolean
  isPasswordRecovery: boolean
  signUp: (
    email: string,
    password: string,
    fullName?: string,
    role?: 'coach' | 'athlete'
  ) => Promise<{ error?: AuthError | null }>
  signIn: (email: string, password: string) => Promise<{ error?: AuthError | null }>
  signOut: () => Promise<{ error?: AuthError | null }>
  resetPassword: (email: string) => Promise<{ error?: AuthError | null }>
  changePassword: (password: string) => Promise<{ error?: AuthError | null }>
  clearPasswordRecovery: () => void
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

interface AuthProviderProps {
  children: React.ReactNode
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(true)
  const [isPasswordRecovery, setIsPasswordRecovery] = useState(false)

  const lastTokenRefreshTimeRef = useRef(0)
  const lastAccessTokenRef = useRef<string | null>(null)

  useEffect(() => {
    const MIN_REFRESH_INTERVAL = 5000 // Minimum 5 seconds between refresh logs

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'PASSWORD_RECOVERY') {
        console.log('[AuthContext] Password recovery')
        setIsPasswordRecovery(true)
        setSession(session)
        setLoading(false)
        // Don't set user during password recovery
        setUser(null)
        return
      }

      if (event === 'SIGNED_IN') {
        setSession(session)
        const accessToken = session?.access_token ?? null
        const isSameSession = accessToken && accessToken === lastAccessTokenRef.current

        if (session?.user) {
          if (!isSameSession) {
            console.log('[AuthContext] Signed in')
          }
          identifyUser(session.user.id, {
            name: session.user.user_metadata?.full_name,
            email: session.user.email ?? '',
          })
          setUser({
            id: session.user.id,
            email: session.user.email!,
          })
        } else {
          console.log('User not found')
        }
        setLoading(false)
        lastAccessTokenRef.current = accessToken
        return
      }

      if (event === 'SIGNED_OUT') {
        console.log('[AuthContext] Sign out')
        resetUser()
        setUser(null)
        setSession(null)
        lastAccessTokenRef.current = null
        setIsPasswordRecovery(false)
        setLoading(false)
        return
      }

      if (event === 'TOKEN_REFRESHED') {
        const now = Date.now()
        const timeSinceLastRefresh = now - lastTokenRefreshTimeRef.current
        if (timeSinceLastRefresh < MIN_REFRESH_INTERVAL && lastTokenRefreshTimeRef.current > 0) {
          console.warn(
            `[AuthContext] Token refresh happening too frequently! Last refresh was ${timeSinceLastRefresh}ms ago.`
          )
        } else {
          console.log('[AuthContext] Token refreshed')
        }

        lastTokenRefreshTimeRef.current = now

        // Only update session if it has actually changed
        // This prevents unnecessary re-renders
        setSession((prevSession) => {
          const shouldUpdate = prevSession?.access_token !== session?.access_token
          if (shouldUpdate) {
            lastAccessTokenRef.current = session?.access_token ?? null
          }
          return shouldUpdate ? session : prevSession
        })
        return
      }

      if (event === 'INITIAL_SESSION') {
        console.log('[AuthContext] Initial session')
        setSession(session)
        if (session?.user) {
          console.log('[AuthContext] Initial session with user')
          setUser({
            id: session.user.id,
            email: session.user.email!,
          })
        } else {
          console.log('[AuthContext] Initial session without user')
          setUser(null)
        }
        setLoading(false)
        lastAccessTokenRef.current = session?.access_token ?? null
        // Don't clear password recovery state on initial session
        return
      }

      // Default case - just update session
      setSession(session)
    })
    return () => subscription.unsubscribe()
  }, [])

  const signUp = async (email: string, password: string, fullName?: string, role?: 'coach' | 'athlete') => {
    // Split full name into first and last names
    const nameParts = fullName?.split(' ') || []
    const firstName = nameParts[0] || ''
    const lastName = nameParts.slice(1).join(' ') || ''

    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          first_name: firstName,
          last_name: lastName,
          signup_source: 'web',
        },
      },
    })

    if (!error && data.user && role) {
      // Add the selected role to user_roles table
      const { error: roleError } = await supabase.from('user_roles').insert({
        user_id: data.user.id,
        role: role,
      })

      if (roleError) {
        console.error('Error adding user role:', roleError)
      }
    }

    // Note: The profile will be created automatically by the database trigger
    // No need to manually create it here since handle_new_user() trigger does this
    if (!error && data.user) {
      identifyUser(data.user.id, {
        name: `${firstName} ${lastName}`,
        email: data.user.email ?? '',
      })
      trackEvent('Sign Up', { signup_source: 'web', role })
    }

    return { error }
  }

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    })
    return { error }
  }

  const signOut = async () => {
    try {
      console.log('Starting signOut process...')

      // Clear local state immediately for better UX
      setUser(null)
      setSession(null)
      resetUser()

      // Try to sign out from Supabase with shorter timeout
      const timeoutPromise = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('SignOut timeout')), 1000)
      )

      const signOutPromise = supabase.auth.signOut()

      try {
        const result = await Promise.race([signOutPromise, timeoutPromise])
        const { error } = result
        console.log('SignOut completed with result:', { error })
        return { error }
      } catch (timeoutError) {
        console.warn('SignOut timed out, but local state cleared:', timeoutError)
        // Local state is already cleared, so user experience is good
        return { error: null }
      }
    } catch (error) {
      console.error('SignOut error:', error)
      return { error: error as any }
    }
  }

  const resetPassword = async (email: string) => {
    const appUrl = process.env.REACT_APP_URL || window.location.origin
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${appUrl}/reset-password`,
    })
    return { error }
  }

  const changePassword = async (password: string) => {
    const { error } = await supabase.auth.updateUser({
      password: password,
    })

    if (!error) {
      // Clear password recovery state after successful password change
      setIsPasswordRecovery(false)
    }

    return { error }
  }

  const clearPasswordRecovery = () => {
    setIsPasswordRecovery(false)
  }

  const value: AuthContextType = {
    user,
    session,
    loading,
    isPasswordRecovery,
    signUp,
    signIn,
    signOut,
    resetPassword,
    changePassword,
    clearPasswordRecovery,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
